import React, { useCallback, useState, type SelectHTMLAttributes } from 'react';
import { NullableDropdownItem, DropdownOption, DropdownGroup } from '../types';
import BiOption from '@wix/da-bi/pkg/Components/BiOption';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { isDropdownGroup, findSelectedItem } from '../helpers';
import { useDropdownItems } from '../hooks/useDropdownItems';
import { useDeepEqualMemo } from '@wix/da-hooks/pkg/useDeepEqualMemo';

export interface Props extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onSelect'> {
  items: NullableDropdownItem[];
  className?: string;
  biData?: BiEvent;
  openBiData?: BiEvent;
  closeBiData?: BiEvent;
  onSelect: (value: any, label: any) => void;
  placeholder?: string;
}

const DUMMY_OPTION = {
  selected: true,
  disabled: true,
  hidden: true,
  value: 'null',
  label: '',
};

const NativeDropdown: React.FC<Props> = ({
  items,
  onSelect,
  onClick,
  biData,
  openBiData = biData,
  closeBiData = biData,
  value,
  ...rest
}) => {
  const { filteredItems } = useDropdownItems(items);
  const selectedItem = findSelectedItem(filteredItems);
  const [isOpen, setOpen] = useState(false);
  const logBiEvent = useBiLogger();
  const memoizedBiData = useDeepEqualMemo(isOpen ? closeBiData : openBiData);

  const onClickWithBi = useCallback(
    (event: React.MouseEvent<HTMLSelectElement>) => {
      memoizedBiData && logBiEvent(memoizedBiData);
      setOpen(open => !open);
      onClick && onClick(event);
    },
    [memoizedBiData, logBiEvent, onClick]
  );

  const renderOption = ({
    value: v,
    label,
    hidden,
    disabled,
    biData: optionBiData,
  }: DropdownOption) => (
    <BiOption
      key={v}
      hidden={hidden}
      value={v}
      disabled={disabled}
      biData={optionBiData}
      label={label}
    >
      {label}{' '}
    </BiOption>
  );

  const renderOptGroup = ({ label, disabled, items: groupItems }: DropdownGroup) => (
    <optgroup key={label} label={label} disabled={disabled}>
      {groupItems.map(renderOption)}
    </optgroup>
  );

  return (
    <select
      onChange={e => onSelect(e.target.value, e)}
      onClick={onClickWithBi}
      value={value ?? (selectedItem ? selectedItem.value : DUMMY_OPTION.value)}
      {...rest}
    >
      {/* to avoid pre-selecting the first option, which makes it non-selectable on mobile, we need to add a dummy option */}
      {!selectedItem && renderOption(DUMMY_OPTION)}
      {filteredItems.map(item =>
        isDropdownGroup(item) ? renderOptGroup(item) : renderOption(item)
      )}
    </select>
  );
};
NativeDropdown.displayName = 'NativeDropdown';

export default NativeDropdown;
